import { Node, mergeAttributes } from "@tiptap/core";
import { PluginKey } from "@tiptap/pm/state";
import { ReactNodeViewRenderer } from "@tiptap/react";
import Suggestion, { SuggestionOptions } from "@tiptap/suggestion";
import request from "graphql-request";
import { queryClient } from "src/app";
import config from "src/config";
import { getEntitySuggestionsDocument } from "./graphql";
import { LinkedMentionNode, MentionNode } from "./mention-node";

export interface MentionNodeAttrs {
  id: string | null;
  label: string | null;
  href: string | null;
}

export type MentionOptions<
  SuggestionItem = any,
  Attrs extends Record<string, any> = MentionNodeAttrs,
> = {
  suggestion: Omit<SuggestionOptions<SuggestionItem, Attrs>, "editor">;
  link?: boolean;
  accessToken?: string;
  siteSlug?: string | undefined;
  subdomain?: string;
};

export const pluginKey = new PluginKey("entityMention");

export const EntityMention = Node.create<MentionOptions>({
  name: "entityMention",
  priority: 101,
  group: "inline",
  inline: true,
  draggable: true,
  selectable: false,
  atom: true,

  addNodeView() {
    if (this.options.link === true) {
      return ReactNodeViewRenderer(LinkedMentionNode, {
        className:
          "inline-block [&.ProseMirror-selectednode_.mention]:bg-primary-200/50 [&.ProseMirror-selectednode_.mention]:border-primary-200/50",
      });
    }
    return ReactNodeViewRenderer(MentionNode, {
      className:
        "inline-block [&.ProseMirror-selectednode_.mention]:bg-primary-200/50 [&.ProseMirror-selectednode_.mention]:border-primary-200/50",
    });
  },

  addOptions() {
    return {
      HTMLAttributes: {},
      deleteTriggerWithBackspace: false,
      suggestion: {
        char: "@",
        pluginKey,
        command: ({ editor, range, props }) => {
          // increase range.to by one when the next node is of type "text"
          // and starts with a space character
          const nodeAfter = editor.view.state.selection.$to.nodeAfter;
          const overrideSpace = nodeAfter?.text?.startsWith(" ");

          if (overrideSpace) {
            range.to += 1;
          }

          editor
            .chain()
            .focus()
            .insertContentAt(range, [
              {
                type: this.name,
                attrs: props,
              },
              {
                type: "text",
                text: " ",
              },
            ])
            .run();

          // get reference to `window` object from editor element, to support cross-frame JS usage
          editor.view.dom.ownerDocument.defaultView
            ?.getSelection()
            ?.collapseToEnd();
        },
        allow: ({ state, range }) => {
          const $from = state.doc.resolve(range.from);
          const type = state.schema.nodes[this.name];
          if (type == null) {
            return false;
          }
          const allow = !!$from.parent.type.contentMatch.matchType(type);

          return allow;
        },
      },
    };
  },

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-id"),
        renderHTML: (attributes) =>
          attributes.id == null
            ? {}
            : {
                "data-id": attributes.id,
              },
      },

      label: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-label"),
        renderHTML: (attributes) =>
          attributes.label == null
            ? {}
            : {
                "data-label": attributes.label,
              },
      },

      href: {
        default: null,
        parseHTML: (element) => element.getAttribute("data-href"),
        renderHTML: (attributes) =>
          attributes.href == null
            ? {}
            : {
                "data-href": attributes.href,
              },
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return [
      "span",
      mergeAttributes({ "data-type": this.name }, HTMLAttributes),
    ];
  },

  parseHTML() {
    return [
      {
        tag: `span[data-type="${this.name}"]`,
      },
    ];
  },

  addKeyboardShortcuts() {
    return {
      Backspace: () =>
        this.editor.commands.command(({ tr, state }) => {
          let isMention = false;
          const { selection } = state;
          const { empty, anchor } = selection;

          if (!empty) {
            return false;
          }

          state.doc.nodesBetween(anchor - 1, anchor, (node, pos) => {
            if (node.type.name === this.name) {
              isMention = true;
              tr.insertText("", pos, pos + node.nodeSize);

              return false;
            }
            return true;
          });

          return isMention;
        }),
    };
  },

  onBeforeCreate() {
    const { subdomain, accessToken, siteSlug } = this.options;
    if (subdomain == null || accessToken == null) {
      return;
    }
    const queryKey = [
      (getEntitySuggestionsDocument.definitions[0] as any)?.name?.value,
      {
        search: "",
        collectionSiteSlug: siteSlug ?? null,
        organisationSubdomain: subdomain,
      },
    ];
    queryClient.fetchQuery({
      queryKey,
      queryFn: ({ queryKey }) =>
        request(
          config.publicApiUrl,
          getEntitySuggestionsDocument,
          queryKey[1] == null ? undefined : queryKey[1],
          accessToken == null
            ? undefined
            : { authorization: `Bearer ${accessToken}` },
        ),
    });
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});
